import axios from 'axios';

// Action Types
export const GET_SOCIAL_FEATURES = 'GET_SOCIAL_FEATURES';
export const GET_SOCIAL_FEATURES_ERROR = 'GET_SOCIAL_FEATURES_ERROR';
export const ACKNOWLEDGE_BADGE_POPUP = 'ACKNOWLEDGE_BADGE_POPUP';
export const TRACK_BADGE_SHARE = 'TRACK_BADGE_SHARE';

/**
 * Get social features data from the API
 * @returns {Function} - Redux thunk action
 */
export const getSocialFeatures = () => async (dispatch) => {
  try {
    const response = await axios.get('/api/social-features/');

    dispatch({
      type: GET_SOCIAL_FEATURES,
      payload: response.data
    });

    return response.data;
  } catch (error) {
    dispatch({
      type: GET_SOCIAL_FEATURES_ERROR,
      payload: error.message
    });

    throw error;
  }
};

/**
 * Mock function to get social features data for testing
 * This will be replaced with actual API calls when backend is ready
 * @returns {Function} - Redux thunk action
 */
export const getMockSocialFeatures = () => (dispatch) => {
  // Mock data that would come from the API
  const mockData = {
    data: {
      // Peer Milestones data
      teammatesNumber: 10,

      // Streak Reminders data
      streakDays: 5,
      streakStart: new Date().toISOString(),

      // Achievement Badges data
      badgeName: 'Consistency Champion',
      badgeProgress: 3,
      badgeTotal: 5,
      badgeDescription: 'Complete Language Practice sessions consistently',
      badgeIconUrl: 'https://cdn-icons-png.flaticon.com/512/2583/2583344.png',
      badges: [
        {
          id: 1,
          name: '1st LP',
          description: 'Completed your first Language Practice session',
          progress: 1,
          total: 1,
          achieved: true,
          iconUrl: 'https://cdn-icons-png.flaticon.com/512/3176/3176341.png'
        },
        {
          id: 2,
          name: 'Consistency Champion',
          description: 'Complete 5 Language Practice sessions',
          progress: 3,
          total: 5,
          achieved: false,
          iconUrl: 'https://cdn-icons-png.flaticon.com/512/2583/2583344.png'
        },
        {
          id: 3,
          name: 'Study Champ',
          description: 'Complete 10 self-study sessions',
          progress: 4,
          total: 10,
          achieved: false,
          iconUrl: 'https://cdn-icons-png.flaticon.com/512/6941/6941710.png'
        },
        {
          id: 4,
          name: 'Perfect Week',
          description: 'Study every day for a week',
          progress: 5,
          total: 7,
          achieved: false,
          iconUrl: 'https://cdn-icons-png.flaticon.com/512/3112/3112946.png'
        },
        {
          id: 5,
          name: 'Word Master',
          description: 'Learn 100 new vocabulary words',
          progress: 78,
          total: 100,
          achieved: false,
          iconUrl: 'https://cdn-icons-png.flaticon.com/512/4359/4359732.png'
        },
        {
          id: 6,
          name: 'Level Up',
          description: 'Advance to the next language level',
          progress: 0,
          total: 1,
          achieved: false,
          iconUrl: 'https://cdn-icons-png.flaticon.com/512/5662/5662990.png'
        }
      ],

      // Flag for badge popup
      showBadgePopup: true,
      badgeId: 1
    }
  };

  dispatch({
    type: GET_SOCIAL_FEATURES,
    payload: mockData
  });

  return mockData;
};

/**
 * Acknowledge badge popup has been shown
 * @param {number} badgeId - ID of the badge that was shown in popup
 * @returns {Function} - Redux thunk action
 */
export const acknowledgeBadgePopup = (badgeId) => async (dispatch, getState) => {
  try {
    // Get token from Redux state
    const { token } = getState().user;

    if (!token) {
      console.error('No authentication token available');
      return false;
    }

    // Make API call with the authorization token in headers
    await axios.put(
      '/api/social-features/acknowledge-badge/',
      { badgeId },
      {
        headers: { Authorization: `Token ${token}` }
      }
    );

    dispatch({
      type: ACKNOWLEDGE_BADGE_POPUP,
      payload: badgeId
    });

    return true;
  } catch (error) {
    console.error('Error acknowledging badge popup:', error);
    return false;
  }
};

/**
 * Track when a badge is shared on social media
 * @param {number} badgeId - ID of the badge that was shared
 * @param {string} platform - Social media platform where badge was shared (e.g., 'LinkedIn')
 * @returns {Function} - Redux thunk action
 */
export const trackBadgeShare = (badgeId, platform) => async (dispatch) => {
  try {
    // In the future, we could implement an API endpoint for tracking shares
    // await axios.post('/api/social-features/track-share/', { badgeId, platform });

    dispatch({
      type: TRACK_BADGE_SHARE,
      payload: { badgeId, platform }
    });

    return true;
  } catch (error) {
    console.error(`Error tracking badge share on ${platform}:`, error);
    return false;
  }
};
